<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery } from '@tanstack/vue-query';
import { routePlansApi, type RoutePlan, type CoverageSlot, type CoverageSummary } from '@/api/route-plans';
import { requestErrorMessage } from '@/utils/api-errors';
import { useGroupFilterVisible } from '@/composables/useGroupFilterVisible';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import RoutePlanCoverageBand from './route-plan-coverage-band.vue';
import {
  UNCATEGORIZED_FILTER_VALUE, attrsFromSlot, buildCards, filterBands, groupCards, hasCategories,
  type CoverageCard,
} from './route-plan-coverage.utils';

// "Cobertura del día": reconciles the plan's day requirements (weekly plans for its career
// and group) against existing shifts and the plan's assignments/visits, as a left-to-right
// flow — Falta cubrir → En proceso → Cubierto — grouped in category bands. Missing slots can
// be quick-added as pending visits (individually or in bulk), prefilled from the requirement.
interface Props {
  modelValue: boolean;
  routePlan: RoutePlan;
  // Hides the quick-add actions. The setup step keeps its visits in a local form that is
  // persisted as a whole, so server-side quick-adds from here would be clobbered; there the
  // dialog is a read-only reconciliation view and "Cargar esperadas" is the import path.
  readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), { readonly: false });
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'saved': [warnings: string[]];
}>();

const { t } = useI18n();

// Same rule as group-filter-select: users who only see one group get no group labels.
const { shouldShow: groupUiVisible } = useGroupFilterVisible();

const selectedCategoryIds = ref<number[]>([]);
const expandedBands = ref<number[]>([]);
const expansionSeeded = ref(false);
const addingIds = ref<Set<number>>(new Set());
const bulkBusy = ref(false);
const bulkProgress = ref({ done: 0, total: 0 });
const errorText = ref('');

const { data: coverage, isPending, isError, refetch } = useQuery({
  queryKey: ['routePlanCoverage', computed(() => props.routePlan.id)],
  queryFn: () => routePlansApi.coverage(props.routePlan.id),
  enabled: computed(() => props.modelValue),
});

const cards = computed<CoverageCard[]>(() => (coverage.value ? buildCards(coverage.value) : []));
const bands = computed(() => groupCards(cards.value));
const showCategories = computed(() => hasCategories(cards.value));
const visibleBands = computed(() => filterBands(bands.value, selectedCategoryIds.value));

const categoryItems = computed(() => {
  const seen = new Map<number, { title: string; priority: number }>();
  cards.value.forEach((card) => {
    if (card.categoryId) {
      seen.set(card.categoryId, {
        title: card.categoryName ?? '', priority: card.categoryPriority ?? -1,
      });
    } else {
      seen.set(UNCATEGORIZED_FILTER_VALUE, { title: t('routePlan.board.noCategory'), priority: -1 });
    }
  });

  return [...seen.entries()]
    .sort(([, a], [, b]) => b.priority - a.priority)
    .map(([value, { title }]) => ({ value, title }));
});

function bandKey(categoryId: number | null): number {
  return categoryId ?? UNCATEGORIZED_FILTER_VALUE;
}

// All bands start expanded, but only on first data per open — refetches after a quick-add
// must not re-expand what the planner collapsed.
function seedExpansion() {
  expandedBands.value = bands.value.map((band) => bandKey(band.categoryId));
  expansionSeeded.value = true;
}

watch(() => props.modelValue, (open) => {
  if (open) {
    selectedCategoryIds.value = [];
    errorText.value = '';
    expansionSeeded.value = false;
    if (bands.value.length) seedExpansion();
  }
});

watch(bands, () => {
  if (!expansionSeeded.value && bands.value.length) seedExpansion();
}, { immediate: true });

const FULL_PERCENT = 100;
const EMPTY_SUMMARY: CoverageSummary = {
  expected: 0, assigned: 0, existingShifts: 0, pending: 0, missing: 0, blocked: 0, extra: 0,
  doubleCoverage: 0,
};

const summary = computed(() => coverage.value?.summary ?? EMPTY_SUMMARY);
const covered = computed(() => summary.value.assigned + summary.value.existingShifts);
const progressPercent = computed(() => (summary.value.expected > 0 ?
  (covered.value / summary.value.expected) * FULL_PERCENT :
  0));

const missingSlots = computed(
  () => (coverage.value?.slots ?? []).filter((slot) => slot.state === 'missing'),
);

function setAdding(weeklyPlanId: number, adding: boolean) {
  const next = new Set(addingIds.value);
  if (adding) next.add(weeklyPlanId);
  else next.delete(weeklyPlanId);
  addingIds.value = next;
}

async function quickAdd(slot: CoverageSlot) {
  if (addingIds.value.has(slot.weeklyPlanId) || bulkBusy.value) return;
  setAdding(slot.weeklyPlanId, true);
  errorText.value = '';
  try {
    await routePlansApi.createVisit(props.routePlan.id, attrsFromSlot(slot));
    emit('saved', []);
    await refetch();
  } catch (error) {
    errorText.value = requestErrorMessage(error, t('routePlan.coverage.addError'));
  } finally {
    setAdding(slot.weeklyPlanId, false);
  }
}

function quickAddCard(card: CoverageCard) {
  if (card.slot) quickAdd(card.slot);
}

// Sequential on purpose: no write burst against the plan, and progress stays meaningful.
// Failures don't stop the batch; they are collected and reported once at the end.
async function createMissingVisits(slots: CoverageSlot[]): Promise<string[]> {
  const failures: string[] = [];
  for (const slot of slots) {
    try {
      await routePlansApi.createVisit(props.routePlan.id, attrsFromSlot(slot));
    } catch (error) {
      failures.push(requestErrorMessage(error, t('routePlan.coverage.addError')));
    }
    bulkProgress.value = { done: bulkProgress.value.done + 1, total: bulkProgress.value.total };
  }

  return failures;
}

function bulkErrorText(failures: string[]): string {
  const unique = [...new Set(failures)];

  return `${t('routePlan.coverage.bulkPartialError', { count: failures.length })} ${unique.join(' · ')}`;
}

async function addAllMissing() {
  const slots = missingSlots.value;
  if (!slots.length || bulkBusy.value) return;
  bulkBusy.value = true;
  errorText.value = '';
  bulkProgress.value = { done: 0, total: slots.length };
  const failures = await createMissingVisits(slots);
  if (failures.length > 0) errorText.value = bulkErrorText(failures);
  emit('saved', []);
  await refetch();
  bulkBusy.value = false;
}
</script>

<template>
  <responsive-dialog
    :model-value="modelValue"
    :max-width="1100"
    :title="t('routePlan.coverage.title')"
    scrollable
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div
      v-if="isPending"
      class="d-flex justify-center pa-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>

    <div
      v-else-if="isError"
      class="d-flex flex-column align-center pa-8 ga-3"
    >
      <v-alert
        type="error"
        variant="tonal"
        density="compact"
      >
        {{ t('routePlan.coverage.loadError') }}
      </v-alert>
      <v-btn
        variant="tonal"
        prepend-icon="mdi-refresh"
        @click="refetch()"
      >
        {{ t('routePlan.coverage.retry') }}
      </v-btn>
    </div>

    <template v-else-if="coverage">
      <div class="text-body-2 text-medium-emphasis mb-3">
        {{ routePlan.humanDate }} · {{ routePlan.humanCareer }}
        <template v-if="groupUiVisible && routePlan.groupName">
          · {{ routePlan.groupName }}
        </template>
      </div>

      <v-alert
        v-if="!coverage.carePlanningEnabled"
        type="info"
        variant="tonal"
        density="compact"
      >
        {{ t('routePlan.coverage.emptyNoCarePlanning') }}
      </v-alert>

      <v-alert
        v-else-if="!cards.length"
        type="info"
        variant="tonal"
        density="compact"
      >
        {{ t('routePlan.coverage.emptyNoRequirements') }}
      </v-alert>

      <template v-else>
        <div class="d-flex align-center ga-3 mb-3">
          <v-progress-linear
            :model-value="progressPercent"
            color="success"
            height="8"
            rounded
            class="flex-grow-1"
          />
          <span class="text-body-2 flex-shrink-0">
            {{ t('routePlan.coverage.progress', {
              covered, total: coverage.summary.expected,
            }) }}
          </span>
        </div>
        <div
          v-if="summary.blocked > 0"
          class="text-caption text-medium-emphasis mb-3"
          data-testid="coverage-blocked-note"
        >
          <v-icon
            icon="mdi-calendar-lock"
            size="x-small"
            class="me-1"
          />
          {{ t('routePlan.coverage.blockedNote', summary.blocked) }}
        </div>

        <template v-if="showCategories">
          <v-select
            v-model="selectedCategoryIds"
            :items="categoryItems"
            :label="t('routePlan.board.categoryFilter')"
            class="mb-3"
            density="compact"
            variant="outlined"
            multiple
            clearable
            hide-details
          />
          <v-expansion-panels
            v-model="expandedBands"
            multiple
            variant="accordion"
          >
            <v-expansion-panel
              v-for="band in visibleBands"
              :key="bandKey(band.categoryId)"
              :value="bandKey(band.categoryId)"
            >
              <v-expansion-panel-title>
                <span class="font-weight-medium">
                  {{ band.categoryName ?? t('routePlan.board.noCategory') }}
                </span>
                <v-chip
                  size="x-small"
                  variant="tonal"
                  class="ms-2"
                >
                  {{ band.total }}
                </v-chip>
              </v-expansion-panel-title>
              <v-expansion-panel-text eager>
                <route-plan-coverage-band
                  :band="band"
                  :readonly="readonly"
                  :adding-ids="addingIds"
                  :busy="bulkBusy"
                  @quick-add="quickAddCard"
                />
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>

        <template v-else>
          <route-plan-coverage-band
            v-for="band in visibleBands"
            :key="bandKey(band.categoryId)"
            :band="band"
            :readonly="readonly"
            :adding-ids="addingIds"
            :busy="bulkBusy"
            @quick-add="quickAddCard"
          />
        </template>

        <v-alert
          v-if="errorText"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          {{ errorText }}
        </v-alert>
      </template>
    </template>
    <template
      v-if="!readonly && coverage?.carePlanningEnabled && cards.length > 0"
      #actions
    >
      <span
        v-if="bulkBusy"
        class="text-body-2 text-medium-emphasis"
      >
        {{ t('routePlan.coverage.addProgress', bulkProgress) }}
      </span>
      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="mdi-playlist-plus"
        :loading="bulkBusy"
        :disabled="!missingSlots.length"
        @click="addAllMissing"
      >
        {{ t('routePlan.coverage.addAllMissing') }}
      </v-btn>
    </template>
  </responsive-dialog>
</template>
